import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from './CreateListingModal.module.css';
import SingleModalBg from '../../assets/dashboard_assets/single_modal.png';
import BulkModalBg from '../../assets/dashboard_assets/bulk_modal.png';
import ModalEllipse from '../../assets/dashboard_assets/modal_ellipse.png';
import { useAmazonSync } from '../../context/AmazonSyncContext';


// Backdrop component
const Backdrop = (props) => {
  return <div className={styles.backdrop} onClick={props.onClose} />;
};

// Modal overlay component
const ModalOverlay = (props) => {
  // If initialType is provided (from dashboard cards), skip directly to mode selection
  const initialStep = props.initialType ? 'mode' : 'type';
  const [step, setStep] = useState(initialStep);
  const [selectedType, setSelectedType] = useState(props.initialType || null); // 'new' or 'existing'
  const [selectedMode, setSelectedMode] = useState(null); // 'single' or 'bulk'
  const [projectName, setProjectName] = useState('');
  const { isSyncing } = useAmazonSync();
  const newListingDisabled = isSyncing; // gated until catalogue sync finishes

  // Reset state when initialType changes (modal reopened with different type)
  useEffect(() => {
    // If we're syncing and the caller wanted New Listing, fall back to the
    // type-selection step so the user sees the disabled card + tooltip
    // rather than being silently routed into a broken flow.
    const forceTypeStep = props.initialType === 'new' && newListingDisabled;
    const initial = forceTypeStep ? null : (props.initialType || null);
    const newStep = initial ? 'mode' : 'type';
    setStep(newStep);
    setSelectedType(initial);
    setSelectedMode(null);
    setProjectName('');
  }, [props.initialType, newListingDisabled]);

  const handleTypeClick = (type) => {
    if (type === 'new' && newListingDisabled) return;
    setSelectedType(type);
    setStep('mode');
  };

  const handleModeClick = (mode) => {
    setSelectedMode(mode);
    if (props.onModeSelect) props.onModeSelect(mode);
  };

  // Allow parent to seed the project name (used by tutorial "Add Sample Name")
  useEffect(() => {
    if (props.sampleProjectName) {
      setProjectName(props.sampleProjectName);
      if (props.onProjectNameChange) props.onProjectNameChange(props.sampleProjectName);
    }
  }, [props.sampleProjectName]);

  const handleProjectNameSubmit = () => {
    if (projectName.trim()) {
      props.onOptionSelect(selectedMode, selectedType === 'new', projectName.trim());
    }
  };

  const handleBack = () => {
    if (selectedMode) {
      setSelectedMode(null);
      setProjectName('');
    } else if (step === 'mode') {
      setStep('type');
      setSelectedType(null);
    }
  };

  // Step 1: New/Existing selection
  if (step === 'type') {
    return (
      <div className={styles.modal}>
        <button onClick={props.onClose} className={styles.closeButton} aria-label="Close Modal">
          <div className={styles.closeIcon}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 1.4L14.6 0L8 6.6L1.4 0L0 1.4L6.6 8L0 14.6L1.4 16L8 9.4L14.6 16L16 14.6L9.4 8L16 1.4Z" fill="#505050"/>
            </svg>
          </div>
        </button>

        <div className={styles.modalContent}>
          <div className={styles.titleSection}>
            <div className={styles.title}>Create Listing</div>
            <div className={styles.titleSeparator}></div>
          </div>

          <div className={styles.cardsContainer}>
            {/* New Listing Card */}
            <div
              className={styles.listingCard}
              onClick={() => handleTypeClick('new')}
              style={{
                backgroundImage: `url(${SingleModalBg})`,
                opacity: newListingDisabled ? 0.45 : 1,
                cursor: newListingDisabled ? 'not-allowed' : 'pointer',
                position: 'relative',
              }}
              title={newListingDisabled ? 'Available once your Amazon catalogue finishes syncing' : ''}
            >
              <div className={styles.cardTextContainer}>
                <div className={styles.cardLabel}>Create</div>
                <div className={styles.cardTitle}>New Listing</div>
              </div>
              <img src={ModalEllipse} alt="" className={styles.modalEllipse} />
              {newListingDisabled && (
                <div style={{
                  position: 'absolute', top: 8, right: 8,
                  background: '#FEFEFF', color: '#2766AA',
                  fontSize: 11, fontWeight: 600,
                  padding: '4px 8px', borderRadius: 12,
                  border: '1px solid #D0E7FF',
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#2766AA', display: 'inline-block' }} />
                  Syncing
                </div>
              )}
            </div>

            {/* Existing Listing Card */}
            <div 
              className={styles.listingCardBulk} 
              onClick={() => handleTypeClick('existing')}
              style={{ backgroundImage: `url(${SingleModalBg})` }}
            >
              <div className={styles.cardTextContainer}>
                <div className={styles.cardLabel}>Upgrade</div>
                <div className={styles.cardTitle}>Existing Listing</div>
              </div>
              <img src={ModalEllipse} alt="" className={styles.modalEllipse} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Single/Bulk selection
  if (step === 'mode') {
    return (
      <div className={`${styles.modal} ${selectedMode ? styles.modalWithProjectName : ''}`}>
        <button onClick={props.onClose} className={styles.closeButton} aria-label="Close Modal">
          <div className={styles.closeIcon}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 1.4L14.6 0L8 6.6L1.4 0L0 1.4L6.6 8L0 14.6L1.4 16L8 9.4L14.6 16L16 14.6L9.4 8L16 1.4Z" fill="#505050"/>
            </svg>
          </div>
        </button>

        <div className={styles.modalContent}>
          <div className={styles.titleSection}>
            <div className={styles.title}>
              {selectedType === 'new' ? 'New Listing Creation' : 'Existing Listing Upgrade'}
            </div>
            <div className={styles.titleSeparator}></div>
          </div>

          <div className={styles.cardsContainer}>
            {/* Single Listing Card */}
            <div
              ref={props.singleListingCardRef}
              className={styles.listingCard}
              onClick={() => handleModeClick('single')}
              style={{ backgroundImage: `url(${SingleModalBg})` }}
            >
              <div className={styles.cardTextContainer}>
                <div className={styles.cardLabel}>Generate</div>
                <div className={styles.cardTitle}>Single Listing</div>
              </div>
              <img src={ModalEllipse} alt="" className={styles.modalEllipse} />
            </div>

            {/* Bulk Listing Card */}
            <div
              className={styles.listingCardBulk}
              onClick={() => handleModeClick('bulk')}
              style={{ backgroundImage: `url(${BulkModalBg})` }}
            >
              <div className={styles.cardTextContainer}>
                <div className={styles.cardLabel}>Generate</div>
                <div className={styles.cardTitle}>Bulk Listing</div>
              </div>
              <img src={ModalEllipse} alt="" className={styles.modalEllipse} />
            </div>
          </div>

          {/* Project Name input appears below cards when a mode is selected */}
          {selectedMode && (
            <div className={styles.projectNameContainer}>
              <input
                ref={props.projectNameInputRef}
                type="text"
                className={styles.projectNameInput}
                placeholder="Enter project name"
                value={projectName}
                onChange={(e) => {
                  setProjectName(e.target.value);
                  if (props.onProjectNameChange) props.onProjectNameChange(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && projectName.trim()) {
                    handleProjectNameSubmit();
                  }
                }}
                autoFocus
              />
              <button
                ref={props.continueButtonRef}
                onClick={handleProjectNameSubmit}
                className={styles.submitButton}
                disabled={!projectName.trim()}
              >
                Continue
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // This should never be reached, but keeping for safety
  return null;
};

// Main Modal component using React Portals
const CreateListingModal = (props) => {
  // Check if running in a browser environment before accessing document
  if (typeof window === 'undefined') {
    return null;
  }

  const portalElement = document.getElementById('modal-root');

  // Ensure the portal element exists before attempting to create a portal
  if (!portalElement) {
    return null;
  }

  // Only render if show is true
  if (!props.show) {
    return null;
  }

  return (
    <React.Fragment>
      {ReactDOM.createPortal(
        <Backdrop onClose={props.onClose} />,
        portalElement
      )}
      {ReactDOM.createPortal(
        <ModalOverlay
          onClose={props.onClose}
          onOptionSelect={props.onOptionSelect}
          title={props.title}
          initialType={props.initialType}
          singleListingCardRef={props.singleListingCardRef}
          projectNameInputRef={props.projectNameInputRef}
          continueButtonRef={props.continueButtonRef}
          onModeSelect={props.onModeSelect}
          sampleProjectName={props.sampleProjectName}
          onProjectNameChange={props.onProjectNameChange}
        />,
        portalElement
      )}
    </React.Fragment>
  );
};

export default CreateListingModal; 